import { useState, useEffect, useCallback } from 'react';
import {
  Bell, MapPin, Navigation, PhoneCall, Copy, X, Car,
  Users, RefreshCw, Trash2, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, axios, showToast, copyToClipboard } from './adminHelpers';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

export const BookingsManager = ({ authHeader }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: authHeader });
      setBookings(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);
  useEffect(() => { const i = setInterval(fetchBookings, 30000); return () => clearInterval(i); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/status?status=${status}`, {}, { headers: authHeader });
      showToast(`Booking ${status}`);
      fetchBookings();
      if (selectedBooking?.id === id) setSelectedBooking(prev => ({ ...prev, status }));
    } catch {
      showToast('Failed to update status', 'error');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, { headers: authHeader });
      showToast('Booking deleted');
      fetchBookings();
      setSelectedBooking(null);
    } catch {
      showToast('Failed to delete', 'error');
    }
  };

  const newBookingsCount = bookings.filter(b => b.status === 'pending' || !b.status).length;

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    if (filter === 'pending') return b.status === 'pending' || !b.status;
    return b.status === filter;
  });

  const BookingCard = ({ booking }) => {
    const isPending = booking.status === 'pending' || !booking.status;
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border-l-4 p-4 mb-3 cursor-pointer transition-all hover:shadow-md ${
          isPending ? 'border-l-orange-500 bg-orange-50/30' :
          booking.status === 'confirmed' ? 'border-l-blue-500' :
          booking.status === 'completed' ? 'border-l-green-500' : 'border-l-gray-300'
        }`}
        onClick={() => setSelectedBooking(booking)}
        data-testid={`booking-card-${booking.id}`}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-gray-900">{booking.full_name}</h3>
            <p className="text-sm text-gray-500">{booking.pickup_date} at {booking.pickup_time}</p>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColors[booking.status] || statusColors.pending}`}>
            {booking.status || 'pending'}
          </span>
        </div>
        <div className="text-sm text-gray-600 mb-3">
          <div className="flex items-center gap-1 mb-1">
            <MapPin className="w-3 h-3 text-green-600 flex-shrink-0" />
            <span className="truncate">{booking.pickup_address}</span>
          </div>
          <div className="flex items-center gap-1">
            <Navigation className="w-3 h-3 text-red-600 flex-shrink-0" />
            <span className="truncate">{booking.dropoff_address}</span>
          </div>
        </div>
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <a href={`tel:${booking.phone}`} onClick={(e) => e.stopPropagation()} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors" data-testid={`call-btn-${booking.id}`}>
            <PhoneCall className="w-4 h-4" /> Call
          </a>
          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(booking.phone, 'Phone'); }} className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors" data-testid={`copy-phone-btn-${booking.id}`}>
            <Copy className="w-4 h-4" /> Copy Phone
          </button>
        </div>
      </div>
    );
  };

  const BookingDrawer = ({ booking, onClose }) => {
    if (!booking) return null;
    return (
      <div className="fixed inset-0 z-50 lg:relative lg:inset-auto" data-testid="booking-drawer">
        <div className="fixed inset-0 bg-black/50 lg:hidden" onClick={onClose} />
        <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl overflow-y-auto lg:relative lg:h-auto lg:shadow-lg lg:rounded-lg">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
            <h2 className="font-bold text-lg">Booking Details</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-drawer-btn"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Status</span>
              <select value={booking.status || 'pending'} onChange={(e) => updateStatus(booking.id, e.target.value)} className={`text-sm px-3 py-1.5 rounded-full font-medium border ${statusColors[booking.status] || statusColors.pending}`} data-testid="booking-status-select">
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Customer</h3>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{booking.full_name}</span></div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Phone</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{booking.phone}</span>
                    <button onClick={() => copyToClipboard(booking.phone, 'Phone')} className="p-1 hover:bg-gray-200 rounded"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-sm">{booking.email}</span></div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Car className="w-4 h-4" /> Trip Details</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-gray-500 text-sm mb-1">Pickup</div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm flex-1">{booking.pickup_address}</span>
                    <button onClick={() => copyToClipboard(booking.pickup_address, 'Pickup')} className="p-1 hover:bg-gray-200 rounded flex-shrink-0"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm mb-1">Dropoff</div>
                  <div className="flex items-start gap-2">
                    <Navigation className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm flex-1">{booking.dropoff_address}</span>
                    <button onClick={() => copyToClipboard(booking.dropoff_address, 'Dropoff')} className="p-1 hover:bg-gray-200 rounded flex-shrink-0"><Copy className="w-3 h-3" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div><div className="text-gray-500 text-sm">Date</div><div className="font-medium">{booking.pickup_date}</div></div>
                  <div><div className="text-gray-500 text-sm">Time</div><div className="font-medium">{booking.pickup_time}</div></div>
                  <div><div className="text-gray-500 text-sm">Vehicle</div><div className="font-medium">{booking.fleet_type || 'Any'}</div></div>
                  <div><div className="text-gray-500 text-sm">Passengers</div><div className="font-medium">{booking.passengers}</div></div>
                </div>
              </div>
            </div>
            {booking.driver_note && (
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Driver Note</h3>
                <p className="text-sm text-gray-700">{booking.driver_note}</p>
              </div>
            )}
            <div className="text-xs text-gray-400 pt-2">
              <div>Submitted: {new Date(booking.created_at).toLocaleString()}</div>
              <div>Booking ID: {booking.id}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-4 border-t">
              <a href={`tel:${booking.phone}`} className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors" data-testid="drawer-call-btn">
                <PhoneCall className="w-5 h-5" /> Call Customer
              </a>
              <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(booking.pickup_address)}&destination=${encodeURIComponent(booking.dropoff_address)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors" data-testid="drawer-maps-btn">
                <MapPin className="w-5 h-5" /> Open Maps
              </a>
            </div>
            <button onClick={() => deleteBooking(booking.id)} className="w-full text-red-600 py-2 text-sm hover:bg-red-50 rounded-lg transition-colors" data-testid="drawer-delete-btn">
              Delete Booking
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="bookings-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          {newBookingsCount > 0 && (
            <span className="flex items-center gap-1 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium animate-pulse" data-testid="new-bookings-badge">
              <Bell className="w-3 h-3" /> {newBookingsCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm border rounded-lg px-3 py-2" data-testid="bookings-filter">
            <option value="all">All Bookings ({bookings.length})</option>
            <option value="pending">Pending ({bookings.filter(b => b.status === 'pending' || !b.status).length})</option>
            <option value="confirmed">Confirmed ({bookings.filter(b => b.status === 'confirmed').length})</option>
            <option value="completed">Completed ({bookings.filter(b => b.status === 'completed').length})</option>
            <option value="cancelled">Cancelled ({bookings.filter(b => b.status === 'cancelled').length})</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchBookings} data-testid="refresh-bookings-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={selectedBooking ? 'hidden lg:block' : ''}>
          {loading ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500"><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />Loading bookings...</div>
          ) : filteredBookings.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500" data-testid="no-bookings-msg">No bookings found.</div>
          ) : (
            <div className="space-y-3">{filteredBookings.map((b) => <BookingCard key={b.id} booking={b} />)}</div>
          )}
        </div>
        {selectedBooking && <BookingDrawer booking={selectedBooking} onClose={() => setSelectedBooking(null)} />}
      </div>
    </div>
  );
};
